import { useEffect, useRef } from 'react';
import { OwcBanner } from '@one/react';

/**
 * Thin wrapper over OwcBanner that keeps a semantic variant API and adds an
 * optional auto-hide convenience the design-system component does not provide.
 *
 * Variant → OwcBanner `status`:
 *   success → positive, warning → warning, error → alarm, info → info
 *
 * - Dismissible via OwcBanner's built-in close button (shown when `onDismiss`
 *   is provided); OwcBanner gives the dismissed element an `alert` role.
 * - Optional auto-hide after `autoHideMs`. The `error` variant never auto-hides;
 *   a failure must be dismissed manually so it is not missed.
 *
 * Presentational: the parent owns the message and visibility.
 *
 * @param {object} props
 * @param {'success'|'warning'|'error'|'info'} [props.variant='info']
 * @param {React.ReactNode} props.children            message content
 * @param {() => void} [props.onDismiss]              show + handle close button
 * @param {number} [props.autoHideMs]                 auto-dismiss delay (ms)
 * @param {object} [props.style]
 * @param {string} [props.className]
 */
const VARIANT_TO_STATUS = {
  success: 'positive',
  warning: 'warning',
  error: 'alarm',
  info: 'info',
};

export default function Banner({
  variant = 'info',
  children,
  onDismiss,
  autoHideMs,
  style,
  className = '',
}) {
  // Keep the latest onDismiss in a ref so the auto-hide timer does not reset on
  // every parent render (callers often pass a fresh inline closure).
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;

  useEffect(() => {
    if (!autoHideMs || !onDismissRef.current) return undefined;
    if (variant === 'error') return undefined;
    const timer = setTimeout(() => onDismissRef.current?.(), autoHideMs);
    return () => clearTimeout(timer);
  }, [autoHideMs, variant]);

  return (
    <OwcBanner
      status={VARIANT_TO_STATUS[variant] || 'info'}
      showClose={Boolean(onDismiss)}
      onDismiss={onDismiss ? () => onDismiss() : undefined}
      className={className}
      style={{ marginTop: '12px', ...style }}
    >
      {children}
    </OwcBanner>
  );
}
